import React, { useEffect, useMemo, useState } from "react";
import { DesktopSheet } from "@/layouts/DesktopSheet";

interface StockTickerDrawerDesktopProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tickers: string[];
  initialTicker?: string | null;
  onSelect: (ticker: string) => void;
}

export const StockTickerDrawerDesktop: React.FC<
  StockTickerDrawerDesktopProps
> = ({ open, onOpenChange, tickers, initialTicker, onSelect }) => {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setQuery(initialTicker ?? "");
    setSelected(initialTicker ?? null);
  }, [open, initialTicker]);

  const visible = useMemo(() => {
    const needle = query.trim().toUpperCase();
    const sorted = [...tickers].sort();
    if (!needle) return sorted.slice(0, 100);
    return sorted.filter((ticker) => ticker.includes(needle)).slice(0, 100);
  }, [query, tickers]);

  return (
    <DesktopSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Trade a tokenized stock"
      width={420}
      footer={
        <button
          type="button"
          disabled={!selected}
          onClick={() => selected && onSelect(selected)}
          className="w-full rounded-lg bg-accent py-3 text-sm font-semibold text-white disabled:opacity-50"
        >
          {selected ? `Trade ${selected}` : "Select a stock"}
        </button>
      }
    >
      <div className="p-4">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value.toUpperCase())}
          placeholder="Search AAPL, NVDA, TSLA"
          className="w-full rounded-lg border border-gray-600 bg-primary/40 p-3 text-sm uppercase text-text-primary focus:border-accent focus:outline-none"
        />
        <div className="mt-3 max-h-[60vh] space-y-1 overflow-y-auto">
          {visible.length === 0 && (
            <p className="py-6 text-center text-sm text-text-secondary">
              No listed stock matches that ticker
            </p>
          )}
          {visible.map((ticker) => (
            <button
              key={ticker}
              type="button"
              onClick={() => setSelected(ticker)}
              className={`w-full rounded-lg px-3 py-2 text-left text-sm font-semibold ${
                selected === ticker
                  ? "bg-sky-500/20 text-sky-100"
                  : "text-text-primary hover:bg-white/5"
              }`}
            >
              {ticker}
            </button>
          ))}
        </div>
      </div>
    </DesktopSheet>
  );
};
